import React, { useRef } from 'react';
import type { TradeHistoryItem } from '../../../types';
import { ItemIcon } from '../../common/ItemIcon';
import { ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { useStore } from '../../../store/useStore';
import { useVirtualizer } from '@tanstack/react-virtual';

interface TradeHistoryListProps {
    history: TradeHistoryItem[];
    className?: string;
}

export const TradeHistoryList = ({ history, className = '' }: TradeHistoryListProps) => {
    const market = useStore(state => state.market);
    const parentRef = useRef<HTMLDivElement>(null);

    const formatRelativeTime = (timestamp: number) => {
        const now = Date.now();
        const diffInSeconds = Math.floor((now - timestamp) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return `${Math.floor(diffInSeconds / 86400)}d ago`;
    };

    const count = history.length;

    // Estimate size: ~62px for item + 4px gap = 66px
    const virtualizer = useVirtualizer({
        count,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 66,
        overscan: 5,
    });

    if (!history || history.length === 0) {
        return (
            <div className={className} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--color-text-muted)', fontStyle: 'italic', border: '1px dashed var(--color-border)', borderRadius: '8px', width: '100%' }}>
                    No trade history yet.
                </div>
            </div>
        );
    }

    return (
        <div
            ref={parentRef}
            className={className}
            style={{
                // Ensure overflow is handled here since className defines it, but we need strictly managed content
                display: 'block', // Override flex if className has it? className has flex-direction column. 
                // If we use virtualizer, we usually just want a scrolling block.
                // .activity-list has display: flex.
                // If we want to use virtualization, we should probably stick to block layout inside?
                // But wait, the virtualizer just renders a tall div for scroll height.
                // let's leave className styles but ensure items are absolute.
            }}
        >
            <div
                style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    width: '100%',
                    position: 'relative',
                }}
            >
                {virtualizer.getVirtualItems().map((virtualItem) => {
                    const item = history[virtualItem.index];
                    if (!item) return null;
                    const marketItem = market[item.itemId];
                    const isSell = item.type === 'sell';
                    const totalValue = item.amount * item.pricePerUnit;

                    return (
                        <div
                            key={item.id}
                            className="history-item"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: `${virtualItem.size - 4}px`, // Gap handling: item height is smaller than slot
                                transform: `translateY(${virtualItem.start}px)`,
                            }}
                        >
                            <div className="history-icon">
                                <ItemIcon id={item.itemId} size={28} />
                                <div className={`type-indicator ${isSell ? 'sell' : 'buy'}`}>
                                    {isSell ? <ArrowUpRight size={10} strokeWidth={3} /> : <ArrowDownLeft size={10} strokeWidth={3} />}
                                </div>
                            </div>

                            <div className="history-details">
                                <div className="history-name">
                                    {marketItem ? marketItem.name : item.itemId}
                                </div>
                                <div className="history-meta">
                                    <span>{formatRelativeTime(item.timestamp)}</span>
                                    <span>•</span>
                                    <span>{item.amount.toLocaleString()}x</span>
                                </div>
                            </div>

                            <div className="history-values">
                                <div className="history-total">
                                    {isSell ? '+' : '-'}{totalValue.toLocaleString()} G
                                </div>
                                <div className="history-price">
                                    @ {item.pricePerUnit} G
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
